//@flow
import React, { useState } from 'react'

import { Button, Input, Select } from 'components/common'
import { useObserver } from '~/core/utils/hooks'
import { useFilterTooltipStyles } from './styles'
import { useI18n } from '~/core/i18n'
import { Filter } from './Filter'

import type { ComparismType } from './Filter'

type ComparismOption = {
  label: string,
  value: ComparismType,
  allowedComparisms?: ComparismType[]
}

type TableFilterTooltipProps = {
  filter: Filter | null,
  columnsOptions: ComparismOption[],
  onChange: (value: Filter) => void
}

const customSelectStyles = {
  ...Select.DefaultStyle,
  container: provided => ({
    ...provided,
    width: 120
  })
}

const comparismOptions: ComparismOption[] = [
  { label: 'common.table.filter.equals', value: 'equals' },
  { label: 'common.table.filter.unequals', value: 'unequals' },
  { label: 'common.table.filter.includes', value: 'includes' }
]

const TableFilterTooltip = ({ filter: filterValue, columnsOptions, onChange, ...props }: TableFilterTooltipProps) => {
  const [msg] = useI18n()

  const getDefaultFilter = (filter: Filter | null) => ({
    column: columnsOptions.find(({ value }) => value === filter?.column) || columnsOptions[0],
    type: comparismOptions.find(({ value }) => value === filter?.type) || comparismOptions[0],
    value: filter?.value || '',
    isDisabled: filter?.isDisabled
  })
  const [filter, setFilter] = useState(getDefaultFilter(filterValue))
  const classes = useFilterTooltipStyles()

  useObserver(filterValue, () => setFilter(getDefaultFilter(filterValue)))

  const handleUpdateFilter = value => {
    setFilter(filter => ({
      ...filter,
      ...value
    }))
  }
  const handleSave = e => {
    e.preventDefault()
    onChange(
      new Filter({
        id: filterValue?.id,
        column: filter.column.value,
        type: filter.type.value,
        value: filter.value,
        isDisabled: filter.isDisabled
      })
    )
  }

  return (
    <form onSubmit={handleSave} {...props}>
      <Select
        id="filter-column-select"
        label={msg('common.table.filter.column')}
        options={columnsOptions}
        value={filter.column}
        onChange={column => handleUpdateFilter({ column })}
      />
      <div className={classes.filterTooltipValueContainer}>
        <Select
          id="filter-comparism-select"
          label={msg('common.table.filter.value')}
          options={comparismOptions
            .filter(({ value }) =>
              filter.column.allowedComparisms ? filter.column.allowedComparisms.includes(value) : true
            )
            .map(({ value, label }) => ({ value, label: msg(label) }))}
          styles={customSelectStyles}
          value={{ value: filter.type.value, label: msg(filter.type.label) }}
          onChange={type => handleUpdateFilter({ type })}
        />
        <Input
          id="filter-value-input"
          value={filter.value}
          onChange={({ target }) => handleUpdateFilter({ value: target.value })}
        />
      </div>
      <Button disabled={!filter.value} id="filter-save-btn" type="submit" onClick={handleSave}>
        {msg('common.button.save')}
      </Button>
    </form>
  )
}

export default TableFilterTooltip
